import React, { useEffect, useMemo, useState } from 'react';

import { ApolloProvider } from '@apollo/react-hooks';
import uuid from 'uuid';

import { IBaseProps } from '@rbi-ctg/frontend';
import { getStoredOtpCredentials } from 'state/auth/hooks/use-account-authentication';
import { useLocale } from 'state/intl';
import { useGatewayFlags } from 'state/launchdarkly';
import { useLogger } from 'state/logger';

import { getClient } from './client';
import { withI18nContext } from './links/with-i18n-context';
import { withLogger } from './links/with-logger';
import { withSessionId } from './links/with-session-id';

export const GraphQLProvider = ({ children }: IBaseProps) => {
  const { language, region } = useLocale();
  const gatewayFlags = useGatewayFlags();
  const client = useMemo(() => getClient(language, region), [gatewayFlags]); // eslint-disable-line react-hooks/exhaustive-deps
  const logger = useLogger();

  // Keep a stable session id for the Apollo link without writing to OTP storage.
  // Auth flows persist their own sessionId via storeOtpCredentials when starting OTP.
  const [graphqlSessionId] = useState(() => {
    const storedSessionId = getStoredOtpCredentials()?.sessionId;
    const initialSessionId = storedSessionId ?? uuid();
    withSessionId.setSessionId(initialSessionId);
    return initialSessionId;
  });

  const sessionId = getStoredOtpCredentials()?.sessionId ?? graphqlSessionId;

  // update stateful links
  useEffect(() => {
    withI18nContext.setLocale(language, region);
  }, [client, language, region]);

  useEffect(() => {
    withSessionId.setSessionId(sessionId);
  }, [sessionId]);

  useEffect(() => {
    withLogger.setLogger(logger);
  }, [logger]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export { useLazySanityQuery, useSanityQuery } from './hooks/use-sanity-query';
